<script lang="ts">
  /**
   * "Confirm it is you." Rendered once, at the app root, for every guarded
   * action — item 13's failure mode is a modal, then the action proceeds.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { stepUp } from "./stepup.svelte";

  /**
   * Autofocus the field the moment the challenge appears. Without it the person
   * has to hunt for the input after already deciding to proceed, which is exactly
   * the friction that trains people to click through guards without reading them.
   */
  function focus(node: HTMLInputElement) {
    node.focus();
  }
</script>

{#if stepUp.confirming}
  <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="cf-title">
    <div class="card--hero box">
      <span class="badge badge--warn" aria-hidden="true"><Icon name="alert" size={16} /></span>
      <h2 id="cf-title">This is a live project</h2>
      <p class="sub">
        <strong>{stepUp.confirming.project}</strong> is not deployed by Fabric — it runs on its own.
        Acting on it here takes effect immediately, for everyone using it.
      </p>
      <p class="sub">Type <code>{stepUp.confirming.expected}</code> to go ahead.</p>

      <!-- svelte-ignore a11y_autofocus -->
      <input
        class="type"
        use:focus
        bind:value={stepUp.confirming.typed}
        placeholder={stepUp.confirming.expected}
        aria-label="Type the service name to confirm"
        autocomplete="off"
        spellcheck="false"
        onkeydown={(e) => e.key === "Enter" && stepUp.confirmTyped()}
      />

      {#if stepUp.error}
        <p class="err"><Icon name="alert" size={12} /> {stepUp.error}</p>
      {/if}

      <div class="acts">
        <button class="lnk" onclick={() => stepUp.cancel()} disabled={stepUp.busy}>Cancel</button>
        <button
          class="btn-primary"
          onclick={() => stepUp.confirmTyped()}
          disabled={stepUp.busy || stepUp.confirming.typed.trim() !== stepUp.confirming.expected}
        >
          {stepUp.busy ? "Working…" : "I am sure"}
        </button>
      </div>
    </div>
  </div>
{/if}

{#if stepUp.pending}
  <!-- The backdrop is not a dismiss target. A destructive action held mid-flight
       should end in a deliberate yes or a deliberate no, not in a stray click. -->
  <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="su-title">
    <div class="card--hero box">
      <span class="badge" aria-hidden="true"><Icon name="shield" size={16} /></span>
      <h2 id="su-title">Confirm it is you</h2>
      <p class="sub">
        {stepUp.pending.label} changes something the whole household sees, so it needs a fresh sign-in.
        Your session is more than five minutes old.
      </p>

      {#if stepUp.error}
        <p class="err"><Icon name="alert" size={12} /> {stepUp.error}</p>
      {/if}

      <div class="acts">
        <button class="lnk" onclick={() => stepUp.cancel()} disabled={stepUp.busy}>Cancel</button>
        <button class="btn-primary" onclick={() => stepUp.confirm()} disabled={stepUp.busy}>
          {stepUp.busy ? "Confirming…" : "Confirm with Google"}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .type {
    width: 100%;
    margin: 4px 0 2px;
    padding: 9px 11px;
    font-family: var(--mono, ui-monospace, monospace);
    font-size: 0.7812rem;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--ln);
    border-radius: var(--r-control);
  }
  .type:focus {
    outline: 2px solid var(--acc);
    outline-offset: 1px;
  }
  code {
    padding: 1px 5px;
    font-family: var(--mono, ui-monospace, monospace);
    font-size: 0.72rem;
    color: var(--tx);
    background: var(--bg);
    border-radius: 5px;
  }
  /* Amber, never red — the palette never rides green-vs-red, and the glyph and
     the wording already carry the meaning without the colour. */
  .badge--warn {
    color: var(--warn);
  }
  .scrim {
    position: fixed;
    inset: 0;
    z-index: 90;
    display: grid;
    place-items: center;
    padding: 24px;
    background: rgb(0 0 0 / 0.5);
    backdrop-filter: var(--glass-blur, blur(6px));
  }
  .box {
    width: min(390px, 100%);
    padding: 22px 22px 18px;
    text-align: center;
  }
  .badge {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    margin: 0 auto 10px;
    border-radius: var(--r-control);
    background: var(--wash);
    color: var(--acc);
  }
  h2 {
    margin: 0 0 6px;
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: -0.3px;
    color: var(--tx);
  }
  .err {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 10px 0 0;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--warn);
  }
  .acts {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    margin-top: 16px;
  }
  .lnk {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }
  .lnk:hover {
    color: var(--acc);
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }
</style>
